import { keyBy } from 'lodash';
import { v4 as uuid } from 'uuid';
import { logger } from 'services';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';
import { QueryCombinerType } from '../organisms/tasks/combiner';
import { FieldFilter } from '../organisms/tasks/filters';
const DEFAULT_FILTER = {
    type: QueryCombinerType.All,
    criteria: []
};
export const to_filter = (filter, { preserveOrder = false } = {})=>{
    var _ref, _ref1;
    var _filter_criteria, _filter_nestedFilter;
    const validCriteria = (_ref = filter === null || filter === void 0 ? void 0 : (_filter_criteria = filter.criteria) === null || _filter_criteria === void 0 ? void 0 : _filter_criteria.filter(FieldFilter.isValid)) !== null && _ref !== void 0 ? _ref : [];
    const withOrder = (entry, criterion)=>preserveOrder ? {
            ...entry,
            order: validCriteria.indexOf(criterion)
        } : entry;
    const nativeFields = validCriteria.filter((x)=>!x.field.userCreated && x.field.fieldName !== 'template').map((x)=>withOrder(ApiFilter.toNativeFieldFilterFormat(x), x));
    const templateField = validCriteria.find((x)=>x.field.fieldName === 'template');
    if (templateField) {
        nativeFields.push(withOrder(ApiFilter.toNativeFieldFilterFormat(templateField), templateField));
    }
    const nonEmptyNestedFilters = (_ref1 = filter === null || filter === void 0 ? void 0 : (_filter_nestedFilter = filter.nestedFilter) === null || _filter_nestedFilter === void 0 ? void 0 : _filter_nestedFilter.filter((nestedFilter)=>{
        var _ref;
        var _nestedFilter_criteria, _nestedFilter_nestedFilter;
        const hasValidCriteria = nestedFilter === null || nestedFilter === void 0 ? void 0 : (_nestedFilter_criteria = nestedFilter.criteria) === null || _nestedFilter_criteria === void 0 ? void 0 : _nestedFilter_criteria.some(FieldFilter.isValid);
        const hasValidNestedFilters = (_ref = (_nestedFilter_nestedFilter = nestedFilter.nestedFilter) === null || _nestedFilter_nestedFilter === void 0 ? void 0 : _nestedFilter_nestedFilter.some((subNested)=>{
            var _subNested_criteria;
            return subNested === null || subNested === void 0 ? void 0 : (_subNested_criteria = subNested.criteria) === null || _subNested_criteria === void 0 ? void 0 : _subNested_criteria.some(FieldFilter.isValid);
        })) !== null && _ref !== void 0 ? _ref : false;
        return hasValidCriteria || hasValidNestedFilters;
    })) !== null && _ref1 !== void 0 ? _ref1 : [];
    const finalNestedFilters = nonEmptyNestedFilters.length > 0 ? nonEmptyNestedFilters.map((nested)=>to_filter(nested, {
            preserveOrder
        })) : [];
    return {
        nativeFields,
        customFields: validCriteria.filter((x)=>x.field.userCreated).map((x)=>withOrder(ApiFilter.toCustomFieldFilterFormat(x), x)),
        match: filter.type,
        nestedFilter: finalNestedFilters
    };
};
/** URL persistence helper — stamps `order` so from_filter_v2 can restore row order. */ export const to_url_filter = (filter)=>to_filter(filter, {
        preserveOrder: true
    });
const tryParseNativeFieldFilter = (filter, lookup)=>{
    const field = lookup[filter.name];
    try {
        return field ? ApiFilter.parse(filter, field) : null;
    } catch (error) {
        logger.error('failed to parse criteria', {
            filter,
            error: error
        });
        return null;
    }
};
const tryParseCustomFieldFilter = (filter, lookup)=>{
    const byIdLookup = keyBy(Object.values(lookup), 'fieldId');
    const field = byIdLookup[filter.fieldId];
    try {
        const result = field ? ApiFilter.parse(filter, field) : null;
        return result;
    } catch (error) {
        logger.error('failed to parse criteria', {
            filter,
            error: error
        });
        return null;
    }
};
export const from_filter = (lookup)=>(query)=>{
        var _query_match;
        if (Object.keys(lookup).length === 0) {
            return DEFAULT_FILTER;
        }
        const result = {
            type: (_query_match = query.match) !== null && _query_match !== void 0 ? _query_match : QueryCombinerType.All,
            criteria: [],
            nestedFilter: []
        };
        if (query.nativeFields) {
            const nativeFieldFilters = query.nativeFields.map((filter)=>tryParseNativeFieldFilter(filter, lookup)).filter((x)=>x !== null).map((x)=>({
                    ...x,
                    id: uuid()
                }));
            // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            result.criteria.push(...nativeFieldFilters);
        }
        if (query.customFields) {
            const customFieldFilters = query.customFields.map((filter)=>tryParseCustomFieldFilter(filter, lookup)).filter((x)=>x !== null).map((x)=>({
                    ...x,
                    id: uuid()
                }));
            // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            result.criteria.push(...customFieldFilters);
        }
        if (query.nestedFilter) {
            result.nestedFilter = query.nestedFilter.map((nestedQuery)=>from_filter(lookup)(nestedQuery));
        }
        return result;
    };
export const from_filter_v2 = (lookup)=>(query)=>{
        var _query_match, _query_nativeFields, _query_customFields;
        if (Object.keys(lookup).length === 0) {
            return DEFAULT_FILTER;
        }
        const result = {
            type: (_query_match = query.match) !== null && _query_match !== void 0 ? _query_match : QueryCombinerType.All,
            criteria: [],
            nestedFilter: []
        };
        const nativeFieldFilters = ((_query_nativeFields = query.nativeFields) !== null && _query_nativeFields !== void 0 ? _query_nativeFields : []).map((rawFilter)=>{
            const parsed = tryParseNativeFieldFilter(rawFilter, lookup);
            return parsed ? {
                filter: parsed,
                order: rawFilter.order
            } : null;
        }).filter((x)=>x !== null);
        const customFieldFilters = ((_query_customFields = query.customFields) !== null && _query_customFields !== void 0 ? _query_customFields : []).map((rawFilter)=>{
            const parsed = tryParseCustomFieldFilter(rawFilter, lookup);
            return parsed ? {
                filter: parsed,
                order: rawFilter.order
            } : null;
        }).filter((x)=>x !== null);
        const allFilters = [
            ...nativeFieldFilters,
            ...customFieldFilters
        ];
        // If every entry carries an order stamp (written by to_url_filter /
        // encodeFilterStringify), restore the original insertion order. Otherwise
        // fall back to the legacy native-first ordering so old URLs are not broken.
        const hasOrderInfo = allFilters.every((x)=>x.order !== undefined);
        if (hasOrderInfo) {
            allFilters.sort((a, b)=>{
                var _a_order, _b_order;
                return ((_a_order = a.order) !== null && _a_order !== void 0 ? _a_order : 0) - ((_b_order = b.order) !== null && _b_order !== void 0 ? _b_order : 0);
            });
        }
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        result.criteria.push(...allFilters.map((x)=>x.filter));
        if (query.nestedFilter) {
            result.nestedFilter = query.nestedFilter.map((nestedQuery)=>from_filter_v2(lookup)(nestedQuery));
        }
        return result;
    };
export const getApiFilterTransformer = (lookup)=>({
        from: (simpleSearchFilter)=>from_filter(lookup)(simpleSearchFilter),
        to: to_filter
    });
